import imageCompression from 'browser-image-compression';

import { getFileNameWithoutExtension } from '~/lib/util/file.util';

import { SMTImageCompression } from '~/lib/constants/SMT.constants';
import { isDevEnvironment } from '~/lib/util/environment.util';

const COMPRESSED_IMAGE_TYPE = 'image/jpeg';
const COMPRESSED_IMAGE_EXTENSION = 'jpg';

export interface CompressionMetadata {
  originalMediaType: string;
  originalSizeKB: number;
  compressedMediaType: string;
  compressedSizeKB: number;
  compressionDurationMs: number;
  compressionRate: number;
  wasCompressed: boolean;
}

/**
 * Compresses an SMT image file using browser-image-compression
 * Uses balanced quality settings to target 400-600KB file size range
 * Applies 1920px max dimension and converts to JPEG format
 * @param file - The image file to compress
 * @returns A Promise that resolves to an object containing the compressed File (or original if compression doesn't occur) and compression metadata
 * @throws Error if compression fails
 */
export const compressSMTImage = async (
  file: File,
): Promise<{ file: File; metadata: CompressionMetadata }> => {
  const compressionStartTime = performance.now();
  const originalMediaType = file.type;
  const originalSizeKB = file.size / 1024;
  const compressionRate = SMTImageCompression.initialQuality;

  // Default metadata for cases where compression doesn't occur
  const defaultMetadata: CompressionMetadata = {
    compressedMediaType: originalMediaType,
    compressedSizeKB: originalSizeKB,
    compressionDurationMs: 0,
    compressionRate,
    originalMediaType,
    originalSizeKB,
    wasCompressed: false,
  };

  if (!file.type.startsWith('image/')) {
    console.warn('File is not an image, skipping compression');
    return { file, metadata: defaultMetadata };
  }

  const { initialQuality, maxSizeMB } = SMTImageCompression;

  const options = {
    fileType: COMPRESSED_IMAGE_TYPE,
    initialQuality,
    maxSizeMB,
    maxWidthOrHeight: SMTImageCompression.maxDimension,
    useWebWorker: true,
  };

  const compressedBlob = await imageCompression(file, options);
  const compressionEndTime = performance.now();
  const compressionDurationMs = compressionEndTime - compressionStartTime;

  if (compressedBlob.size >= file.size) {
    console.warn(
      'Compressed file is larger than original, using original file',
    );
    return {
      file,
      metadata: {
        ...defaultMetadata,
        compressionDurationMs,
      },
    };
  }

  const originalName = file.name;
  const nameWithoutExt = getFileNameWithoutExtension(originalName);
  const newFileName = `${nameWithoutExt}.${COMPRESSED_IMAGE_EXTENSION}`;

  const compressedFile = new File([compressedBlob], newFileName, {
    lastModified: Date.now(),
    type: COMPRESSED_IMAGE_TYPE,
  });

  const compressedSizeKB = compressedFile.size / 1024;

  if (isDevEnvironment) {
    const originalSizeKBFormatted = originalSizeKB.toFixed(2);
    const compressedSizeKBFormatted = compressedSizeKB.toFixed(2);
    const reduction = (
      ((file.size - compressedFile.size) / file.size) *
      100
    ).toFixed(1);
    console.log(
      `Image compressed: ${originalSizeKBFormatted}KB → ${compressedSizeKBFormatted}KB (${reduction}% reduction), filename: ${newFileName}`,
    );
  }

  return {
    file: compressedFile,
    metadata: {
      compressedMediaType: COMPRESSED_IMAGE_TYPE,
      compressedSizeKB,
      compressionDurationMs,
      compressionRate,
      originalMediaType,
      originalSizeKB,
      wasCompressed: true,
    },
  };
};
